import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAppStore } from './store/useAppStore';

// Layouts
import PublicLayout from './components/layout/PublicLayout';

// Public Pages
import HomePage from './pages/public/HomePage';
import ServicesPage from './pages/public/ServicesPage';
import ServiceDetailPage from './pages/public/ServiceDetailPage';
import GalleryPage from './pages/public/GalleryPage';
import ContactPage from './pages/public/ContactPage';
import GetQuotePage from './pages/public/GetQuotePage';
import BlogPage from './pages/public/BlogPage';
import FAQPage from './pages/public/FAQPage';
import TestimonialsPage from './pages/public/TestimonialsPage';
import LoginPage from './pages/public/LoginPage';
import AboutPage from './pages/public/AboutPage';

// Portal Routes (guarded)
import AdminRoutes from './routes/AdminRoutes';
import CustomerRoutes from './routes/CustomerRoutes';
import ContractorRoutes from './routes/ContractorRoutes';

const PlaceholderPage: React.FC<{ title: string }> = ({ title }) => (
  <PublicLayout>
    <div className="page-header" style={{ paddingTop: '7rem' }}>
      <h1 style={{ color: 'white', fontSize: '2.5rem', fontWeight: 800 }}>{title}</h1>
    </div>
    <div style={{ textAlign: 'center', padding: '4rem 2rem', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚧</div>
      <p>This page is designed and ready for content.</p>
    </div>
  </PublicLayout>
);

const ProtectedRoute: React.FC<{ children: React.ReactNode; roles?: string[] }> = ({ children, roles }) => {
  const { isAuthenticated, user } = useAppStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles && user && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
};

const App: React.FC = () => {
  const { theme } = useAppStore();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    // Favicons are declared statically in public/index.html (ico + 16/32 png +
    // apple-touch). This previously rewrote them at runtime to the wide white
    // logo, which silently overrode those tags — a wordmark is unreadable at
    // 16px and vanishes against a light tab bar. Do not reintroduce.
    document.title = 'Cates Roofing & Contracting';
  }, []);

  // Global scroll-reveal observer (Elo-style entrance animations)
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    const observe = () => {
      document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale')
        .forEach((el) => { if (!el.classList.contains('revealed')) io.observe(el); });
    };

    observe();

    const mo = new MutationObserver(observe);
    mo.observe(document.body, { childList: true, subtree: true });

    return () => { io.disconnect(); mo.disconnect(); };
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* Public */}
        <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
        <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
        <Route path="/services/:slug" element={<PublicLayout><ServiceDetailPage /></PublicLayout>} />
        <Route path="/gallery" element={<PublicLayout><GalleryPage /></PublicLayout>} />
        <Route path="/testimonials" element={<PublicLayout><TestimonialsPage /></PublicLayout>} />
        <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
        <Route path="/blog/:slug" element={<PlaceholderPage title="Blog Article" />} />
        <Route path="/faq" element={<PublicLayout><FAQPage /></PublicLayout>} />
        <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
        <Route path="/get-quote" element={<PublicLayout><GetQuotePage /></PublicLayout>} />
        <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
        <Route path="/financing" element={<PlaceholderPage title="Financing Options" />} />
        <Route path="/service-areas" element={<PlaceholderPage title="Service Areas" />} />
        <Route path="/emergency" element={<PlaceholderPage title="Emergency Roofing" />} />
        <Route path="/careers" element={<PlaceholderPage title="Careers" />} />
        <Route path="/privacy" element={<PlaceholderPage title="Privacy Policy" />} />
        <Route path="/terms" element={<PlaceholderPage title="Terms of Service" />} />

        {/* Auth */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<PlaceholderPage title="Create Account" />} />
        <Route path="/forgot-password" element={<PlaceholderPage title="Reset Password" />} />

        {/* Admin Portal */}
        <Route path="/admin/*" element={
          <ProtectedRoute roles={['admin', 'superadmin']}>
            <AdminRoutes />
          </ProtectedRoute>
        } />

        {/* Customer Portal */}
        <Route path="/portal/*" element={
          <ProtectedRoute roles={['customer', 'admin', 'superadmin']}>
            <CustomerRoutes />
          </ProtectedRoute>
        } />

        {/* Contractor Portal */}
        <Route path="/contractor/*" element={
          <ProtectedRoute roles={['contractor', 'admin']}>
            <ContractorRoutes />
          </ProtectedRoute>
        } />

        <Route path="*" element={<PlaceholderPage title="Page Not Found" />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
